/**
 * useClaimRoom — calls claim-room Edge Function.
 *
 * Cleaner-driven room discovery. Wraps the Edge Function which upserts
 * the room on (building_id, room_number) and creates the floor on the
 * fly if (building_id, derived floor_number) wasn't pre-seeded. Maps
 * the structured error codes to AppError.userMessage so the picker
 * surfaces the right inline message on the right field.
 *
 * Pre-go-live (2026-05-18) — ABC's first contract has no room manifest.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export interface ClaimRoomVars {
  buildingId:     string
  roomNumber:     string
  bedCount:       1 | 2
  bathroomCount:  1 | 2
}

export interface ClaimRoomResult {
  room_id:                 string
  floor_id:                string
  was_created:             boolean
  floor_was_created:       boolean
  floor_count_auto_bumped: boolean
  bed_count:               number
  bathroom_count:          number
  bed_count_changed:       boolean
  bathroom_count_changed:  boolean
}

export function useClaimRoom(): UseMutationResult<ClaimRoomResult, AppError, ClaimRoomVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<ClaimRoomResult, AppError, ClaimRoomVars>({
    mutationFn: async (vars): Promise<ClaimRoomResult> => {
      const { data, error } = await supabase.functions.invoke<ClaimRoomResult>('claim-room', {
        body: {
          building_id:    vars.buildingId,
          room_number:    vars.roomNumber,
          bed_count:      vars.bedCount,
          bathroom_count: vars.bathroomCount,
        },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'CLAIM_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-rooms']      })
      void queryClient.invalidateQueries({ queryKey: ['building-rooms'] })
      void queryClient.invalidateQueries({ queryKey: ['room-board']     })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']       })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_BUILDING':         return 'Pick a building before claiming the room.'
    case 'INVALID_ROOM_NUMBER':      return 'Room number must be 3-4 digits — e.g. 101 for 1st floor room 1.'
    case 'INVALID_BED_COUNT':        return 'Bed count must be single or double.'
    case 'INVALID_BATH_COUNT':       return 'Bathroom count must be single or double.'
    case 'BUILDING_NOT_FOUND':       return 'That building could not be found.'
    case 'BUILDING_INACTIVE':        return 'That building has been deactivated.'
    case 'FORBIDDEN_BUILDING':       return 'You can only claim rooms in your own organization.'
    case 'ROOM_NUMBER_OUT_OF_RANGE': return 'That room number implies a floor higher than this building has. Pick a lower number, or ask an admin to extend the floor count.'
    case 'RATE_LIMITED':             return 'Too many room claims in a row. Pause for a moment.'
    case 'AUDIT_FAILED':             return 'Saved partially — the audit trail did not write. Tell an admin so the row is reconciled.'
    default:                         return detail || 'Could not claim the room.'
  }
}
